import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { createFunnelOpportunity } from "../../services/funnelOpportunities";
import { getFunnels } from "../../services/funnels";
import { getFunnelCards } from "../../services/funnelCards";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    selectField: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    paper: {
        borderRadius: 12,
        boxShadow: "0px 25px 50px -12px rgba(0, 0, 0, 0.25) !important",
        border: "1px solid rgba(0, 0, 0, 0.05)"
    }
}));

const OpportunitySchema = Yup.object().shape({
    title: Yup.string().required("Obrigatório"),
    funnelId: Yup.number().required("Obrigatório"),
    funnelCardId: Yup.number().required("Obrigatório"),
});

const TicketFunnelModal = ({ open, onClose, ticket, onOpportunityCreated }) => {
    const classes = useStyles();
    const [funnels, setFunnels] = useState([]);
    const [cards, setCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);

    const initialState = {
        title: "",
        description: "",
        funnelId: "",
        funnelCardId: "",
        value: "",
    };

    useEffect(() => {
        if (open) {
            loadFunnels();
        }
    }, [open]);

    const loadFunnels = async () => {
        try {
            const { data } = await getFunnels();
            setFunnels(data.funnels || data);
        } catch (err) {
            toastError(err);
        }
    };

    const handleFunnelChange = async (funnelId, setFieldValue) => {
        setFieldValue("funnelId", funnelId);
        setFieldValue("funnelCardId", "");
        setCards([]);

        if (!funnelId) return;

        setLoadingCards(true);
        try {
            const { data } = await getFunnelCards(funnelId);
            setCards(data);
        } catch (err) {
            toastError(err);
        } finally {
            setLoadingCards(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleSave = async (values) => {
        try {
            const payload = {
                ...values,
                contactId: ticket.contact.id,
                value: values.value ? parseFloat(values.value) : null,
            };

            const { data } = await createFunnelOpportunity(payload);
            toast.success("Oportunidade criada com sucesso!");
            if (onOpportunityCreated) {
                onOpportunityCreated(data);
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
            <DialogTitle>Adicionar ao Funil Kanban</DialogTitle>
            <Formik
                initialValues={initialState}
                validationSchema={OpportunitySchema}
                onSubmit={(values, { setSubmitting }) => {
                    handleSave(values);
                    setSubmitting(false);
                }}
            >
                {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                    <Form>
                        <DialogContent dividers>
                            <Field
                                as={TextField}
                                label="Título da Oportunidade"
                                name="title"
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                autoFocus
                            />

                            <FormControl variant="outlined" margin="dense" fullWidth error={touched.funnelId && Boolean(errors.funnelId)}>
                                <InputLabel>Funil</InputLabel>
                                <Select
                                    value={values.funnelId}
                                    onChange={(e) => handleFunnelChange(e.target.value, setFieldValue)}
                                    label="Funil"
                                >
                                    <MenuItem value="">
                                        <em>Selecione um Funil</em>
                                    </MenuItem>
                                    {funnels.map((funnel) => (
                                        <MenuItem key={funnel.id} value={funnel.id}>
                                            {funnel.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                error={touched.funnelCardId && Boolean(errors.funnelCardId)}
                                disabled={!values.funnelId || loadingCards}
                            >
                                <InputLabel>{loadingCards ? "Carregando..." : "Estágio (Card)"}</InputLabel>
                                <Select
                                    value={values.funnelCardId}
                                    onChange={(e) => setFieldValue("funnelCardId", e.target.value)}
                                    label="Estágio (Card)"
                                    name="funnelCardId"
                                >
                                    <MenuItem value="">
                                        <em>Selecione um Estágio</em>
                                    </MenuItem>
                                    {cards.map((card) => (
                                        <MenuItem key={card.id} value={card.id}>
                                            {card.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Field
                                as={TextField}
                                label="Valor Estimado"
                                name="value"
                                type="number"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />

                            <Field
                                as={TextField}
                                label="Descrição / Observações"
                                name="description"
                                multiline
                                rows={3}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting}
                                variant="contained"
                                className={classes.btnWrapper}
                            >
                                Salvar e Continuar
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default TicketFunnelModal;
